import { skipToken } from '@apollo/client/react'
import { produce } from 'immer'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { withQuery } from 'ufo'
import { openModelPreviewTestIneligibleDialog } from 'src/components/GenerationModel/ModelPermissionDialog'
import {
  encodeArtworkForImport,
  encodeTaskForImport,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/importEncode'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import { executeMarkArtwork } from 'src/services/apollo/artwork'
import { useListChatEditingScenes } from 'src/services/apollo/chatEditing'
import type {
  ArtworkDetailFragment,
  DetailedTaskParameters,
} from 'src/services/apollo/generated/graphql'
import { MarkType } from 'src/services/apollo/generated/graphql'
import {
  fetchGetGenerationModelByVersionId,
  useGetGenerationModelByVersionIdForArtwork,
} from 'src/services/apollo/generation-model'
import { isGeneratedByWorkflow } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import {
  isModelPreviewTest,
  parseModelPermissions,
} from 'src/utils/generationModel/modelPermissions'
import { formatQs } from 'src/utils/qs'
import {
  isArtFilterTask,
  isI2vProTask,
  isReferenceVideoTask,
  isVideoTask,
} from 'src/utils/task'
import { startEventGroup } from 'src/utils/tracker'
import { trackArtwork } from '../common'
import type { ParsedParamsForArtworkDetail } from './parser'

/**
 * remove not applicable parameters, like not accessible lora
 * TODO: Migrate to parsedTaskParameters
 * @param taskParameters
 * @returns
 */
export const filterTaskParameter = async (
  taskParameters?: DetailedTaskParameters,
) => {
  // getGenerationModelByVersionId will returns null for lora that the user do not have access to
  const loraVersionInfos = new Map(
    await Promise.all(
      Object.keys(taskParameters?.parameters?.lora ?? {}).map(
        async versionId =>
          [
            versionId,
            (await fetchGetGenerationModelByVersionId({ id: versionId })).data,
          ] as const,
      ),
    ),
  )
  return produce(taskParameters, taskParameters => {
    if (taskParameters?.parameters?.lora) {
      const lora = taskParameters?.parameters?.lora
      for (const key of Object.keys(lora)) {
        if (loraVersionInfos.get(key)?.generationModelVersion == null) {
          delete lora[key]
        }
      }
    }
    if (taskParameters?.parameters?.loraParameters) {
      const loraParameters = taskParameters?.parameters?.loraParameters
      taskParameters.parameters.loraParameters = loraParameters.filter(
        ({ versionId }: { versionId: string }) =>
          loraVersionInfos.get(versionId)?.generationModelVersion != null,
      )
    }
  })
}

type ArtworkDeriveButtonsProps = {
  artwork: ArtworkDetailFragment
  parsedTaskParameters?: ParsedParamsForArtworkDetail
  /**
   * @deprecated Migrate to parsedArtworkParameters
   */
  taskParameters?: DetailedTaskParameters
  isSinglePage?: boolean
} & StyleProps

export function ArtworkDeriveButtons(props: ArtworkDeriveButtonsProps) {
  // useGetGenerationModelByVersionIdForArtwork suspends on cache miss in Apollo 4.
  // Without this boundary, the suspension bubbles up to ArtworkDetailDialog's
  // <Suspense fallback={<LoadingPage />}>, showing a full-page loader.
  return (
    <Suspense>
      <ArtworkDeriveButtonsContent {...props} />
    </Suspense>
  )
}

function ArtworkDeriveButtonsContent(props: ArtworkDeriveButtonsProps) {
  const { artwork, taskParameters, parsedTaskParameters, className } = props
  const { t } = useTranslation(['artwork'])

  const navigate = useNavigate()
  const [selectedArtwork] = useState<ArtworkDetailFragment>(artwork)

  // Pre-load chat editing scenes for validation (cached by Apollo)
  const { data: chatEditingScenes } = useListChatEditingScenes({})

  const taskType = parsedTaskParameters?.type
  // Only generator type tasks have a modelId that should be queried
  const taskModelId =
    taskType === 'generator' ? parsedTaskParameters?.modelId : undefined
  const { data: { model } = {} } = useGetGenerationModelByVersionIdForArtwork(
    taskModelId ?? skipToken,
  )
  const eligibleToGen = parseModelPermissions(model?.userPermission).canGenerate

  const useAsReference = async () => {
    if (!eligibleToGen && isModelPreviewTest(model)) {
      openModelPreviewTestIneligibleDialog()
      return
    }

    const artwork = selectedArtwork

    trackArtwork('use_as_refer_btn_click', artwork)

    // Handle art filter tasks - navigate to filter page with filter params
    // TODO: Migrate to parsedTaskParameters
    if (isArtFilterTask(taskParameters)) {
      const params = taskParameters?.parameters
      const filterId = params?.inputs?.filterId
      const strength = params?.inputs?.strength

      const goToUrl = withQuery('/generator/image/enhancement', {
        filterId,
        strength,
      })

      try {
        trackArtwork('use_as_refer', artwork)
        executeMarkArtwork({
          artworkId: artwork.id,
          type: MarkType.Inspire,
          target: true,
        })
        startEventGroup('generate_source')
        navigate(goToUrl)
      } catch (err: any) {
        toast.error(err?.message)
      }
      return
    }

    const filteredTaskParameters = await filterTaskParameter(taskParameters)

    const goToUrl = `/generator/image?${formatQs({
      importType: 'use_as_reference',
      artwork: encodeArtworkForImport(artwork),
      task: encodeTaskForImport(filteredTaskParameters),
    })}`

    // Check if this is a chat editing preset scene
    // Validate against pre-loaded valid scene IDs
    const sceneId = taskParameters?.parameters?.sceneId
    const validSceneIds = chatEditingScenes?.map(s => s.sceneId) ?? []
    if (sceneId && validSceneIds.includes(sceneId)) {
      window.open(`/generator/preset/${sceneId}`)
      return
    }

    try {
      trackArtwork('use_as_refer', artwork)

      executeMarkArtwork({
        artworkId: artwork.id,
        type: MarkType.Inspire,
        target: true,
      })

      // fallback to default use as reference action
      startEventGroup('generate_source')
      navigate(goToUrl)
    } catch (err: any) {
      toast.error(err?.message)
    }
  }

  const onGenerateNewArtwork = async () => {
    const artwork = selectedArtwork
    trackArtwork('generate_new_artwork_btn_click', artwork)
    startEventGroup('generate_source')
  }

  return (
    <div
      className={cx(
        'flex flex-row gap-2',
        className,
        taskParameters ? 'block' : 'hidden',
      )}
    >
      {/* Workflow/i2vPro/ref2v generation does not support using as ref */}
      {!isGeneratedByWorkflow(artwork) &&
        !isI2vProTask(taskParameters) &&
        !isReferenceVideoTask(taskParameters) && (
          <UIButton
            variant="filled"
            color="theme"
            className="rounded-full px-6 h-11 text-sm font-semibold text-white flex-1"
            onPress={() => useAsReference()}
            busy="auto"
          >
            {t('artwork:use-as-reference.label')}
          </UIButton>
        )}
      {!isVideoTask(taskParameters) && (
        <UILink
          variant="outlined"
          color="theme"
          className="rounded-full px-6 h-11 text-sm font-semibold flex-1 text-center"
          href={'/generator/image'}
          onClick={() => onGenerateNewArtwork()}
        >
          {t('artwork:generate-new-artwork.label')}
        </UILink>
      )}
    </div>
  )
}
